/**
 * Cart V4 — Virtual Cart (Drop-in replacement for m-cart-v3.js)
 *
 * Same UI, same CSS classes, same Liquid drawer — instant operations.
 * Items stored in JS array + localStorage. Zero Shopify API calls until checkout.
 *
 * Reuses: m-cart-v3.css, m-cart-v3-drawer.liquid (all snippets unchanged)
 * Replaces: m-cart-v3.js (swap the script tag, done)
 */

/* ═══════════════════════════════════════════════════════════════
   Collection image — custom.collection_image_product (same as Liquid cards)
   ═══════════════════════════════════════════════════════════════ */

/** handle → URL from fetch (empty string = fetched, no metafield script) */
const _collectionImageResolvedCache = new Map();

/**
 * URL from cart config (SSR cart lines) or PDP script tag; empty if unset.
 * @param {string} handle
 * @returns {string}
 */
function getCollectionImageOverride(handle) {
  if (!handle || typeof document === 'undefined') return '';
  try {
    const cfgEl = document.getElementById('m-cart-v3-config');
    if (cfgEl) {
      const cfg = JSON.parse(cfgEl.textContent);
      const u = cfg.collectionImageByHandle?.[handle];
      if (typeof u === 'string' && u) return u;
    }
  } catch { /* */ }
  try {
    const pathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (pathMatch && pathMatch[1] === handle) {
      const s = document.getElementById('m-product-collection-image');
      if (s) {
        const u = JSON.parse(s.textContent);
        if (typeof u === 'string' && u) return u;
      }
    }
  } catch { /* */ }
  return '';
}

/**
 * Parses product page HTML for m-product-collection-image (same as product-form.liquid).
 * @param {string} handle
 * @returns {Promise<string>}
 */
async function fetchCollectionImageFromProductPage(handle) {
  if (!handle) return '';
  const root = window.Shopify?.routes?.root || '/';
  try {
    const res = await fetch(`${root}products/${handle}`, {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin'
    });
    if (!res.ok) return '';
    const html = await res.text();
    const m = html.match(/<script[^>]*\bid=["']m-product-collection-image["'][^>]*>([\s\S]*?)<\/script>/i);
    if (!m || !m[1]) return '';
    const u = JSON.parse(m[1].trim());
    return typeof u === 'string' && u ? u : '';
  } catch {
    return '';
  }
}

/**
 * Sync sources first, then optional HTML fetch (Shopify .js has no metafields).
 * @param {string} handle
 * @param {string} fallback
 * @returns {Promise<string>}
 */
async function resolveCollectionImageUrl(handle, fallback = '') {
  if (!handle) return fallback;
  let u = getCollectionImageOverride(handle);
  if (u) return u;
  if (_collectionImageResolvedCache.has(handle)) {
    const c = _collectionImageResolvedCache.get(handle);
    return c || fallback;
  }
  u = await fetchCollectionImageFromProductPage(handle);
  _collectionImageResolvedCache.set(handle, u || '');
  return u || fallback;
}

/**
 * Sync display URL for a line: config + fetch-cache beat stale item.image from localStorage.
 * @param {string} handle
 * @param {string} storedImage
 * @returns {string}
 */
function getLineItemDisplayImage(handle, storedImage) {
  if (!handle) return storedImage || '';
  const sync = getCollectionImageOverride(handle);
  if (sync) return sync;
  const mem = _collectionImageResolvedCache.get(handle);
  if (typeof mem === 'string' && mem) return mem;
  return storedImage || '';
}

/* ═══════════════════════════════════════════════════════════════
   Price Resolver — fetches product data from Shopify JSON
   ═══════════════════════════════════════════════════════════════ */

   class PriceResolver {
    constructor() {
      /** @type {Map<string, Object>} variantId → product data cache */
      this._cache = new Map();
      /** @type {Map<string, Object>} handle → full product JSON cache */
      this._productCache = new Map();
    }
  
    /**
     * Captures pricing from Kaching DOM on PDP.
     * @param {number} variantId
     * @returns {{ regularPrice: number, subscriptionPrice: number|null, subscriptionDiscount: number, compareAtPrice: number|null }|null}
     */
    captureFromDOM(variantId) {
      const el = document.querySelector(`.All_product_selector__js[data-id="${variantId}"]`);
      if (!el) return null;
      const p = (v) => { if (!v) return null; const n = parseFloat(v); return Number.isNaN(n) ? null : Math.round(n * 100); };
      const sub = p(el.getAttribute('data-price'));
      const reg = p(el.getAttribute('data-compare-price')) || sub;
      if (!sub && !reg) return null;
      return {
        regularPrice: reg,
        subscriptionPrice: sub,
        subscriptionDiscount: parseInt(el.getAttribute('data-subscription-discount') || '0', 10),
        compareAtPrice: null
      };
    }
  
    /**
     * Fetches full product JSON from Shopify.
     * @param {string} handle
     * @returns {Promise<Object|null>}
     */
    async fetchProduct(handle) {
      if (!handle) return null;
      if (this._productCache.has(handle)) return this._productCache.get(handle);
      try {
        const root = window.Shopify?.routes?.root || '/';
        // Use .js endpoint: prices in cents, includes selling_plan_allocations
        const res = await fetch(`${root}products/${handle}.js`);
        if (!res.ok) return null;
        const product = await res.json();
        if (product?.id) { this._productCache.set(handle, product); return product; }
        return null;
      } catch { return null; }
    }
  
    /**
     * Resolves full product data (display + pricing + selling plans) for a variant.
     * @param {number} variantId
     * @param {string} handle
     * @returns {Promise<Object|null>}
     */
    async resolveFullProduct(variantId, handle) {
      if (!handle) return null;
      const cacheKey = `${handle}:${variantId}`;
      if (this._cache.has(cacheKey)) return this._cache.get(cacheKey);
  
      const product = await this.fetchProduct(handle);
      if (!product) return null;
      const variant = product.variants?.find((v) => String(v.id) === String(variantId));
      if (!variant) return null;
  
      const fallbackImg = product.image?.src || (product.images?.length > 0 ? product.images[0].src : '');
      const image = await resolveCollectionImageUrl(product.handle, fallbackImg);
  
      // Filter variants by bundle quantities if available (Kaching bridge)
      let variants = product.variants || [];
      const bq = ProductScraper._getBundleQuantities(product.id);
      if (bq) {
        const filtered = ProductScraper._filterVariantsByBundleQty(variants, bq);
        if (filtered) variants = filtered;
      }
  
      const result = {
        productId: product.id,
        title: product.title || '',
        variantTitle: variant.title === 'Default Title' ? '' : (variant.title || ''),
        image,
        productUrl: `/products/${product.handle}`,
        handle: product.handle,
        variants,
        priceData: {
          // .js endpoint returns prices in cents — no conversion needed
          regularPrice: variant.price || 0,
          subscriptionPrice: null,
          subscriptionDiscount: 0,
          compareAtPrice: variant.compare_at_price || null
        }
      };
      this._cache.set(cacheKey, result);
      return result;
    }
  
    /**
     * Resolves pricing. DOM first, JSON fallback.
     * @param {number} variantId
     * @param {string} [handle]
     * @returns {Promise<Object>}
     */
    async resolve(variantId, handle) {
      const dom = this.captureFromDOM(variantId);
      if (dom) return dom;
      if (handle) {
        const p = await this.fetchProduct(handle);
        if (p) {
          const v = p.variants?.find((x) => String(x.id) === String(variantId));
          if (v) {
            return {
              // .js endpoint returns prices in cents — no conversion needed
              regularPrice: v.price || 0,
              subscriptionPrice: null, subscriptionDiscount: 0,
              compareAtPrice: v.compare_at_price || null
            };
          }
        }
      }
      return { regularPrice: 0, subscriptionPrice: null, subscriptionDiscount: 0, compareAtPrice: null };
    }
  }
  
  /* ═══════════════════════════════════════════════════════════════
     Product Data Scraper (DOM → product info)
     ═══════════════════════════════════════════════════════════════ */
  
  const ProductScraper = {
    /**
     * Scrapes product data from an upsell card DOM element.
     * @param {HTMLElement} card
     * @param {number} variantId
     * @returns {Object}
     */
    fromUpsellCard(card, variantId) {
      const pc = card.querySelector('.m-cart-v3-upsell-prices');
      const reg = parseInt(pc?.dataset.regularPrice || '0', 10);
      const cmp = parseInt(pc?.dataset.comparePrice || '0', 10);
      const sub = parseInt(pc?.dataset.subscriptionPrice || '0', 10);
      const disc = parseInt(card.dataset.subscriptionDiscount || '0', 10);
      const planId = card.dataset.sellingPlanId || null;
      const imgEl = card.querySelector('.m-cart-v3-upsell-image');
      const nameEl = card.querySelector('.m-cart-v3-upsell-name');
      const handle = card.dataset.handle || '';
  
      return {
        variantId,
        productId: parseInt(card.dataset.productId || '0', 10),
        handle,
        title: nameEl?.textContent?.trim() || '',
        variantTitle: '',
        image: imgEl?.src || imgEl?.getAttribute('src') || '',
        productUrl: handle ? `/products/${handle}` : '',
        sellingPlanId: planId ? parseInt(planId, 10) : null,
        priceData: {
          regularPrice: reg,
          subscriptionPrice: sub || null,
          subscriptionDiscount: disc,
          compareAtPrice: cmp > reg ? cmp : null
        }
      };
    },
  
    /**
     * Scrapes product data from the PDP.
     * @param {number} variantId
     * @returns {Object|null}
     */
    fromPDP(variantId) {
      const productJson = this._getProductJSON();
      if (productJson) {
        const variant = productJson.variants?.find((v) => String(v.id) === String(variantId));
        if (variant) {
          let image = productJson.featured_image || productJson.image
            || (productJson.images?.length > 0 ? productJson.images[0] : '');
          const colOverride = getCollectionImageOverride(productJson.handle || ProductScraper._getHandleFromURL());
          if (colOverride) image = colOverride;
  
          // Get variants — prefer Kaching deal-bar filtered set, fall back to all product variants
          let rawVariants = this._getKachingDealBarVariants(productJson);
  
          // Fallback: filter by bundle_quantities metafield from DOM
          if (!rawVariants) {
            const bq = this._getBundleQuantities(productJson.id);
            if (bq && productJson.variants) {
              rawVariants = this._filterVariantsByBundleQty(productJson.variants, bq);
            }
          }
  
          if (!rawVariants) rawVariants = productJson.variants || [];
  
          const variants = rawVariants.map((v) => ({
            id: v.id,
            title: this._cleanTitle(v.title || (v.options ? v.options.join(' / ') : `Variant ${v.id}`)),
            available: v.available !== undefined ? v.available : (v.availableForSale !== false),
            price: v.price || 0,
            compareAtPrice: v.compare_at_price || v.compareAtPrice || null,
            sellingPlanId: v.sellingPlanId || null,
            subscriptionPrice: v.subscriptionPrice || null
          }));
  
          return {
            variantId,
            productId: productJson.id,
            handle: productJson.handle || this._getHandleFromURL(),
            title: productJson.title || '',
            variantTitle: variant.title === 'Default Title' ? ''
              : (variant.title || (variant.options ? variant.options.join(' / ') : '')),
            image: typeof image === 'string' ? image : (image?.src || ''),
            productUrl: productJson.url || `/products/${productJson.handle || ''}`,
            variants,
            priceData: {
              regularPrice: variant.price || 0,
              subscriptionPrice: null, subscriptionDiscount: 0,
              compareAtPrice: variant.compare_at_price || variant.compareAtPrice || null
            }
          };
        }
      }
      return null;
    },
  
    /**
     * Reads custom.bundle_quantities metafield from DOM.
     * Looks for data-bundle-quantities attribute on product form or kaching element.
     * @param {number} productId
     * @returns {number[]|null} e.g. [1, 3, 6]
     */
    // Clean variant title: "Deep Sleep Stack - 3 Bottles" → "3 Bottles"
    _cleanTitle(t) {
      const idx = t.lastIndexOf(' - ');
      return idx > 0 ? t.substring(idx + 3) : t;
    },
  
    _getBundleQuantities(productId) {
      // Try product form data attribute
      const form = document.querySelector('form[action*="/cart/add"] [data-bundle-quantities]')
        || document.querySelector('[data-bundle-quantities]');
      if (form) {
        const raw = form.getAttribute('data-bundle-quantities');
        if (raw) {
          const nums = raw.split(',').map((n) => parseInt(n.trim(), 10)).filter((n) => n > 0);
          if (nums.length > 0) return nums;
        }
      }
      // Try Kaching deal bars as quantity source
      const pid = String(productId);
      const el = document.querySelector(`kaching-bundle[product-id="${pid}"] kaching-bundles-block`);
      if (el) {
        try {
          const block = JSON.parse(el.getAttribute('deal-block'));
          if (block?.dealBars) {
            const qtys = block.dealBars.map((db) => {
              const m = db.title.match(/(\d+)/);
              return m ? parseInt(m[1], 10) : 0;
            }).filter((n) => n > 0);
            if (qtys.length > 0) return qtys;
          }
        } catch { /* */ }
      }
      return null;
    },
  
    /**
     * Filters product variants by bundle quantities.
     * Maps quantity numbers to variant option values like "1 Bottle", "3 Bottles".
     * @param {Array} variants
     * @param {number[]} quantities e.g. [1, 3, 6]
     * @returns {Array}
     */
    _filterVariantsByBundleQty(variants, quantities) {
      const filtered = [];
      for (const qty of quantities) {
        const match = variants.find((v) => {
          const title = v.title || (v.options ? v.options.join(' / ') : '');
          const m = title.match(/^(\d+)/);
          return m && parseInt(m[1], 10) === qty;
        });
        if (match) filtered.push(match);
      }
      return filtered.length > 0 ? filtered : null;
    },
  
    /**
     * Reads Kaching deal-bar config to get only the displayed bundle variants.
     * Kaching uses mixed ID formats in defaultVariantsV2 (short nanoIds like "d5Kq"
     * AND full UUIDs). We collect ALL unique variant IDs for this product, then
     * match them to deal bars by title/quantity to get proper labels.
     * @param {Object} productJson
     * @returns {Array|null}
     */
    _getKachingDealBarVariants(productJson) {
      if (!productJson?.id) return null;
      const pid = String(productJson.id);
      const el = document.querySelector(`kaching-bundle[product-id="${pid}"] kaching-bundles-block`);
      if (!el) return null;
      try {
        const block = JSON.parse(el.getAttribute('deal-block'));
        const product = JSON.parse(el.getAttribute('product'));
        if (!block?.dealBars || !product?.variants) return null;
  
        const defaults = block.defaultVariantsV2 || [];
        const productGID = `gid://shopify/Product/${pid}`;
  
        // Build a map: dealBarId → first variant numeric ID (for this product)
        const dealBarToVariant = new Map();
        for (const d of defaults) {
          if (d.productGID === productGID && d.variantGIDs?.[0]) {
            const numId = parseInt(d.variantGIDs[0].replace('gid://shopify/ProductVariant/', ''), 10);
            if (numId && !dealBarToVariant.has(d.dealBarId)) {
              dealBarToVariant.set(d.dealBarId, numId);
            }
          }
        }
  
        // For each deal bar, find its variant: try direct ID match first,
        // then fallback to matching quantity number in title vs variant options
        const usedVariantIds = new Set();
        const dealBarVariants = [];
  
        for (const db of block.dealBars) {
          let variantId = dealBarToVariant.get(db.id) || dealBarToVariant.get(db.nanoId) || null;
  
          // Fallback: match by quantity in title ("3 Bottles" → find variant with "3" in options)
          if (!variantId) {
            const qtyMatch = db.title.match(/(\d+)/);
            if (qtyMatch) {
              const qtyStr = qtyMatch[1];
              for (const v of product.variants) {
                const optStr = (v.options || []).join(' ');
                if (optStr.startsWith(qtyStr) && !usedVariantIds.has(v.id)) {
                  variantId = v.id;
                  break;
                }
              }
            }
          }
  
          if (!variantId) continue;
          const v = product.variants.find((pv) => pv.id === variantId);
          if (!v) continue;
          usedVariantIds.add(variantId);
  
          const sp = v.sellingPlans?.[0] || null;
          dealBarVariants.push({
            id: v.id,
            title: ProductScraper._cleanTitle(db.title),
            available: v.availableForSale !== false,
            price: v.price,
            compareAtPrice: v.compareAtPrice || null,
            sellingPlanId: sp?.id || null,
            subscriptionPrice: sp?.price || null
          });
        }
        return dealBarVariants.length > 0 ? dealBarVariants : null;
      } catch { return null; }
    },
  
    _getProductJSON() {
      const scripts = document.querySelectorAll('script[type="application/json"]');
      for (const s of scripts) {
        // Skip our cart config
        if (s.id === 'm-cart-v3-config') continue;
        try {
          const d = JSON.parse(s.textContent);
          if (d?.product?.variants) return d.product;
          if (d?.variants && d?.id && !d.freeShippingThreshold) return d;
        } catch { /* skip */ }
      }
      if (window.ShopifyAnalytics?.meta?.product) return window.ShopifyAnalytics.meta.product;
      return null;
    },
  
    _getHandleFromURL() {
      const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
      return m ? m[1] : '';
    }
  };
  
  /* ═══════════════════════════════════════════════════════════════
     Virtual Cart (State Manager)
     ═══════════════════════════════════════════════════════════════ */
  
  class VirtualCart {
    static STORAGE_KEY = 'm-cart-v4';
    static EXPIRY_MS = 24 * 60 * 60 * 1000;
    static VERSION = 3;
  
    constructor(config) {
      this.config = config;
      this.priceResolver = new PriceResolver();
      this.items = [];
      this._listeners = new Set();
      this._hydrating = false;
      this._load();
    }
  
    // ── Persistence ──
  
    _load() {
      try {
        const raw = localStorage.getItem(VirtualCart.STORAGE_KEY);
        if (!raw) return;
        const data = JSON.parse(raw);
        if (data.version !== VirtualCart.VERSION) { this._clear(); return; }
        if (Date.now() - data.createdAt > VirtualCart.EXPIRY_MS) { this._clear(); return; }
        this.items = Array.isArray(data.items) ? data.items : [];
      } catch { this._clear(); }
    }
  
    /**
     * Hydrates virtual cart from Shopify's real cart when localStorage is empty.
     * Fetches /cart.js and imports any existing line items.
     * Clears the real Shopify cart after import so Liquid SSR stays clean.
     * @returns {Promise<boolean>} true if items were imported
     */
    async hydrateFromShopify() {
      // Only hydrate if virtual cart has no real items
      if (this.getRealItems().length > 0) return false;
      this._hydrating = true;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) return false;
        const shopifyCart = await res.json();
        if (!shopifyCart.items || shopifyCart.items.length === 0) return false;
  
        let imported = 0;
        for (const item of shopifyCart.items) {
          // Skip shipping protection variant
          if (this.config.shippingProtectionVariantId &&
            String(item.variant_id) === String(this.config.shippingProtectionVariantId)) continue;

          const fallbackLineImg = item.featured_image?.url || item.image || '';
          const img = await resolveCollectionImageUrl(item.handle || '', fallbackLineImg);
          const regularPrice = item.original_price || item.price || 0;
          const comparePrice = item.original_line_price && item.quantity > 0
            ? Math.round(item.original_line_price / item.quantity) : null;
          const hasSub = item.selling_plan_allocation != null;
          const subPrice = hasSub ? item.price : null;
          const planId = item.selling_plan_allocation?.selling_plan?.id || null;
  
          this.items.push({
            id: crypto.randomUUID(),
            variantId: item.variant_id,
            productId: item.product_id,
            handle: item.handle || '',
            quantity: item.quantity,
            sellingPlan: planId,
            price: item.price || 0,
            compareAtPrice: comparePrice && comparePrice > (item.price || 0) ? comparePrice : null,
            regularPrice: hasSub ? regularPrice : (item.price || 0),
            subscriptionPrice: subPrice,
            subscriptionDiscount: 0,
            title: item.product_title || item.title || '',
            variantTitle: item.variant_title === 'Default Title' ? '' : (item.variant_title || ''),
            image: img,
            productUrl: item.url || '',
            isShippingProtection: false,
            isGift: item.price === 0 && item.properties?._gift,
            properties: item.properties || {},
            sellingPlanId: planId,
            originalSellingPlanId: planId,
            variants: []
          });
          imported++;
        }
  
        if (imported > 0) {
          this._save();
          // Clear real Shopify cart so Liquid SSR renders empty state (3 upsells) on next load
          fetch(`${root}cart/clear.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }
          }).catch(() => {});
        }
        return imported > 0;
      } catch { return false; }
      finally { this._hydrating = false; }
    }
  
    _save() {
      try {
        const existing = localStorage.getItem(VirtualCart.STORAGE_KEY);
        const createdAt = existing ? (JSON.parse(existing).createdAt || Date.now()) : Date.now();
        localStorage.setItem(VirtualCart.STORAGE_KEY, JSON.stringify({
          items: this.items, createdAt, version: VirtualCart.VERSION
        }));
      } catch { /* storage full */ }
    }
  
    _clear() { localStorage.removeItem(VirtualCart.STORAGE_KEY); this.items = []; }
  
    // ── Events ──
  
    onChange(fn) { this._listeners.add(fn); return () => this._listeners.delete(fn); }
  
    _emit() {
      // Evaluate gifts before saving/rendering so gift state is always current
      this._evaluatingGifts = this._evaluatingGifts || false;
      if (!this._evaluatingGifts) {
        this._evaluatingGifts = true;
        this.evaluateGifts();
        this._evaluatingGifts = false;
      }
      this._save();
      this._listeners.forEach((fn) => { try { fn(); } catch { /* */ } });
    }
  
    // ── Operations ──
  
    /**
     * Add an item to the virtual cart.
     * Each add creates a separate line item (no merging).
     */
    addItem({
      variantId, productId, handle, quantity = 1, sellingPlan = null,
      priceData, title, variantTitle, image, productUrl,
      isShippingProtection = false, isGift = false,
      properties = {}, sellingPlanId = null, variants = [],
      bundleQuantities = null
    }) {
      const price = sellingPlan && priceData.subscriptionPrice
        ? priceData.subscriptionPrice : priceData.regularPrice;
      const compare = sellingPlan && priceData.subscriptionPrice
        ? priceData.regularPrice : priceData.compareAtPrice;
  
      const resolvedPlanId = sellingPlanId || sellingPlan || null;
      this.items.push({
        id: crypto.randomUUID(),
        variantId, productId, handle, quantity, sellingPlan,
        price, compareAtPrice: compare,
        regularPrice: priceData.regularPrice,
        originalCompareAtPrice: priceData.compareAtPrice || null,
        subscriptionPrice: priceData.subscriptionPrice || null,
        subscriptionDiscount: priceData.subscriptionDiscount || 0,
        title, variantTitle, image, productUrl,
        isShippingProtection, isGift, properties,
        sellingPlanId: resolvedPlanId,
        originalSellingPlanId: resolvedPlanId,
        variants: variants || [],
        bundleQuantities: bundleQuantities || null
      });
      this._emit();
    }
  
    removeItem(lineId) {
      this.items = this.items.filter((i) => i.id !== lineId);
      this._emit();
    }
  
    updateQuantity(lineId, qty) {
      const item = this.items.find((i) => i.id === lineId);
      if (item) { item.quantity = Math.max(1, qty); this._emit(); }
    }
  
    switchVariant(lineId, newVariantId, newVariantTitle, priceData, newSellingPlanId = null) {
      const item = this.items.find((i) => i.id === lineId);
      if (!item) return;
  
      // Capture current subscription state BEFORE updating
      const wasSub = item.sellingPlan != null;
      const oldDiscount = item.subscriptionDiscount || 0;
  
      item.variantId = newVariantId;
      item.variantTitle = newVariantTitle;
      item.regularPrice = priceData.regularPrice;
  
      // Calculate subscription price for the new variant:
      // If priceData has it → use it. Otherwise derive from regularPrice + discount %.
      if (priceData.subscriptionPrice) {
        item.subscriptionPrice = priceData.subscriptionPrice;
      } else if (oldDiscount > 0 && priceData.regularPrice) {
        item.subscriptionPrice = Math.round(priceData.regularPrice * (1 - oldDiscount / 100));
      }
  
      item.subscriptionDiscount = priceData.subscriptionDiscount || oldDiscount || 0;
      // Preserve existing selling plan if new variant doesn't provide one
      item.sellingPlanId = newSellingPlanId || item.originalSellingPlanId || null;
      item.originalSellingPlanId = item.sellingPlanId;
  
      // Preserve subscription state: if user was on subscription, stay on subscription
      if (wasSub && item.sellingPlanId && item.subscriptionPrice) {
        item.sellingPlan = item.sellingPlanId;
        item.price = item.subscriptionPrice;
        item.compareAtPrice = item.regularPrice > item.subscriptionPrice ? item.regularPrice : null;
      } else {
        item.sellingPlan = null;
        item.price = item.regularPrice;
        item.compareAtPrice = priceData.compareAtPrice;
      }
  
      this._emit();
    }
  
    toggleSubscription(lineId, planId = null) {
      const item = this.items.find((i) => i.id === lineId);
      if (!item) return;
      if (planId && item.subscriptionPrice) {
        item.sellingPlan = planId;
        item.sellingPlanId = planId;
        item.price = item.subscriptionPrice;
        // Compare at = highest original price (compare_at_price from Shopify, or OTP if no compare_at)
        const compareAt = item.originalCompareAtPrice || item.regularPrice;
        item.compareAtPrice = compareAt > item.subscriptionPrice ? compareAt : null;
      } else {
        item.sellingPlan = null;
        item.price = item.regularPrice;
        // Restore original compare_at_price when switching back to OTP
        const compareAt = item.originalCompareAtPrice || null;
        item.compareAtPrice = compareAt && compareAt > item.regularPrice ? compareAt : null;
      }
      this._emit();
    }
  
    setShippingProtection(enabled) {
      const vid = this.config.shippingProtectionVariantId;
      if (!vid) return;
      this.items = this.items.filter((i) => !i.isShippingProtection);
      if (enabled) {
        let price = this.config.shippingProtectionPrice || 0;
        if (!price) {
          const priceEl = document.querySelector('.md_cart_gua_price');
          if (priceEl) {
            const parsed = parseFloat(priceEl.textContent.replace(/[^0-9.]/g, ''));
            if (!Number.isNaN(parsed)) price = Math.round(parsed * 100);
          }
        }
        if (!price) price = 499; // Fallback $4.99
        this.items.push({
          id: crypto.randomUUID(), variantId: vid, productId: null, handle: null,
          quantity: 1, sellingPlan: null, price, compareAtPrice: null,
          regularPrice: price, subscriptionPrice: null, subscriptionDiscount: 0,
          title: 'Shipping Protection', variantTitle: '', image: '', productUrl: '',
          isShippingProtection: true, isGift: false, properties: {},
          sellingPlanId: null, variants: []
        });
      }
      this._emit();
    }
  
    // ── Gift Management ──
  
    /**
     * Evaluates and manages free gifts based on cart state and gift rules.
     *
     * Three gift trigger types:
     * 1. Product-triggered (kit/general): config.giftRules from metafields
     * 2. Cart value threshold: config.thresholdGift when subtotal >= giftThreshold
     * 3. Subscription: config.subscriptionGift when any item has active subscription
     *
     * Gift layouts:
     * - 'kit': renders inline under the parent product
     * - 'general': renders in separate section at bottom
     */
    evaluateGifts() {
      const rules = this.config.giftRules || [];
      const realItems = this.getRealItems();
      const cartProductIds = new Set(realItems.map((i) => String(i.productId)));
  
      // Collect which gift variant IDs SHOULD be in cart
      const shouldHaveGifts = new Map(); // vid → gift data
  
      // 1. Product-triggered gifts (from metafields)
      for (const rule of rules) {
        const triggerPid = String(rule.pid);
        if (!cartProductIds.has(triggerPid)) continue;
  
        // Kit gifts only show when the trigger product has an active subscription
        const isKit = rule.layout === 'kit';
        if (isKit) {
          const triggerItem = realItems.find((i) => String(i.productId) === triggerPid);
          if (!triggerItem || triggerItem.sellingPlan == null) continue;
        }
  
        for (const gift of (rule.gifts || [])) {
          if (cartProductIds.has(String(gift.pid))) continue;
          shouldHaveGifts.set(String(gift.vid), {
            ...gift,
            triggerProductId: rule.pid,
            layout: rule.layout || 'general'
          });
        }
      }
  
      // 2. Cart value threshold gift (e.g. Free Ebook at $80)
      const thresholdGift = this.config.thresholdGift;
      const subtotal = this.getSubtotal();
      const threshold = this.config.giftThreshold || 8000;
      if (thresholdGift && subtotal >= threshold) {
        const vid = String(thresholdGift.vid);
        if (!shouldHaveGifts.has(vid) && !cartProductIds.has(String(thresholdGift.pid))) {
          shouldHaveGifts.set(vid, {
            ...thresholdGift,
            triggerProductId: null,
            layout: 'general'
          });
        }
      }
  
      // 3. Subscription gift (when any item has active subscription)
      const subGift = this.config.subscriptionGift;
      if (subGift && this.hasSubscription()) {
        const vid = String(subGift.vid);
        if (!shouldHaveGifts.has(vid) && !cartProductIds.has(String(subGift.pid))) {
          shouldHaveGifts.set(vid, {
            ...subGift,
            triggerProductId: null,
            layout: 'general'
          });
        }
      }
  
      // Current gifts in cart
      const currentGifts = this.items.filter((i) => i.isGift);
      const currentGiftVids = new Set(currentGifts.map((i) => String(i.variantId)));
  
      // Add missing gifts
      let changed = false;
      for (const [vid, giftData] of shouldHaveGifts) {
        if (!currentGiftVids.has(vid)) {
          this.items.push({
            id: crypto.randomUUID(),
            variantId: giftData.vid,
            productId: giftData.pid,
            handle: giftData.handle || null,
            quantity: 1,
            sellingPlan: null,
            price: 0,
            compareAtPrice: null,
            regularPrice: 0,
            subscriptionPrice: null,
            subscriptionDiscount: 0,
            title: giftData.title,
            variantTitle: '',
            image: giftData.image,
            productUrl: '',
            isShippingProtection: false,
            isGift: true,
            properties: { _gift: true },
            sellingPlanId: null,
            originalSellingPlanId: null,
            variants: [],
            bundleQuantities: null,
            giftTriggerProductId: giftData.triggerProductId,
            giftLayout: giftData.layout
          });
          changed = true;
        }
      }
  
      // Remove gifts that should no longer be in cart
      const beforeLen = this.items.length;
      this.items = this.items.filter((i) => {
        if (!i.isGift) return true;
        return shouldHaveGifts.has(String(i.variantId));
      });
      if (this.items.length !== beforeLen) {
        changed = true;
      }
  
      if (changed) this._save();
    }
  
    // ── Getters ──
  
    getRealItems() { return this.items.filter((i) => !i.isShippingProtection && !i.isGift); }
    getGiftItems() { return this.items.filter((i) => i.isGift); }
    getRealItemCount() { return this.getRealItems().reduce((s, i) => s + i.quantity, 0); }
    getSubtotal() { return this.getRealItems().reduce((s, i) => s + i.price * i.quantity, 0); }
    getFullTotal() { return this.items.reduce((s, i) => s + i.price * i.quantity, 0); }
  
    getCompareTotal() {
      let t = 0;
      this.getRealItems().forEach((i) => {
        if (i.compareAtPrice && i.compareAtPrice > i.price) t += i.compareAtPrice * i.quantity;
      });
      return t;
    }
  
    getTotalSavings() {
      let s = 0;
      this.getRealItems().forEach((i) => {
        if (i.compareAtPrice && i.compareAtPrice > i.price) s += (i.compareAtPrice - i.price) * i.quantity;
      });
      return s;
    }
  
    hasSubscription() { return this.getRealItems().some((i) => i.sellingPlan != null); }
    hasShippingProtection() { return this.items.some((i) => i.isShippingProtection); }
  
    getShippingProtectionPrice() {
      const sp = this.items.find((i) => i.isShippingProtection);
      return sp ? sp.price * sp.quantity : 0;
    }
  
    getCartProductIds() {
      const ids = new Set();
      this.getRealItems().forEach((i) => { if (i.productId) ids.add(String(i.productId)); });
      return ids;
    }
  
    getCartVariantIds() {
      const ids = new Set();
      this.getRealItems().forEach((i) => { if (i.variantId) ids.add(String(i.variantId)); });
      return ids;
    }
  
    // ── Checkout ──
  
    async checkout() {
      // Only add real items + shipping protection — let EG Auto Add handle gifts
      const realItems = this.items.filter((i) => i.variantId && !i.isGift);
      if (realItems.filter((i) => !i.isShippingProtection).length === 0) throw new Error('Cart is empty');
      const root = window.Shopify?.routes?.root || '/';
  
      const clearRes = await fetch(`${root}cart/clear.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }
      });
      if (!clearRes.ok) throw new Error('Failed to clear cart');
  
      // Resolve correct selling plans per variant before adding
      const payload = [];
      for (const i of realItems) {
        const entry = { id: i.variantId, quantity: i.quantity };
        if (i.sellingPlan && i.handle) {
          const resolvedPlan = await this._resolveSellingPlan(i.handle, i.variantId, i.sellingPlan);
          if (resolvedPlan) {
            entry.selling_plan = resolvedPlan;
          } else {
            console.warn('[V4] Checkout: no valid selling plan for variant', i.variantId, '— adding as OTP');
          }
        }
        if (i.properties && Object.keys(i.properties).length > 0) entry.properties = i.properties;
        payload.push(entry);
      }
  
      const addRes = await fetch(`${root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: payload })
      });
      if (!addRes.ok) {
        const err = await addRes.json().catch(() => ({}));
        console.error('[V4] Checkout add failed:', addRes.status, err);
        throw new Error(err.description || err.message || 'Failed to add items');
      }
  
      // Wait for EG Auto Add to process gifts (it listens to cart/add.js)
      await new Promise((r) => setTimeout(r, 1200));
      window.location.href = `${root}checkout`;
    }
  
    /**
     * Resolves the correct selling plan ID for a specific variant.
     * Fetches /products/{handle}.js which includes selling_plan_allocations per variant.
     * Falls back to the original plan ID if resolution fails.
     * @param {string} handle - Product handle
     * @param {number} variantId - The variant ID to find a plan for
     * @param {number} originalPlanId - The selling plan we want to apply
     * @returns {Promise<number|null>} - The resolved selling plan ID
     */
    async _resolveSellingPlan(handle, variantId, originalPlanId) {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const res = await fetch(`${root}products/${handle}.js`);
        if (!res.ok) return originalPlanId;
        const product = await res.json();
  
        const variant = product.variants?.find((v) => v.id === variantId);
        if (!variant || !variant.selling_plan_allocations) return originalPlanId;
  
        // Check if the original plan ID works for this variant
        const exactMatch = variant.selling_plan_allocations.find(
          (a) => a.selling_plan_id === originalPlanId
        );
        if (exactMatch) return originalPlanId;
  
        // Original plan doesn't match — find any selling plan in the same group
        let originalGroupId = null;
        for (const v of product.variants) {
          const alloc = v.selling_plan_allocations?.find(
            (a) => a.selling_plan_id === originalPlanId
          );
          if (alloc) {
            originalGroupId = alloc.selling_plan_group_id;
            break;
          }
        }
  
        if (originalGroupId) {
          const groupMatch = variant.selling_plan_allocations.find(
            (a) => a.selling_plan_group_id === originalGroupId
          );
          if (groupMatch) {
            return groupMatch.selling_plan_id;
          }
        }
  
        // Last resort: use the first available selling plan for this variant
        if (variant.selling_plan_allocations.length > 0) {
          return variant.selling_plan_allocations[0].selling_plan_id;
        }
  
        return null;
      } catch (err) {
        console.warn('[V4] Selling plan resolution failed:', err);
        return originalPlanId;
      }
    }
  
    clear() { this._clear(); this._emit(); }
  }
  
  /* ═══════════════════════════════════════════════════════════════
     Cart Drawer UI (V3-Compatible Output)
     ═══════════════════════════════════════════════════════════════ */
  
  class CartDrawerUI {
    constructor(cart, config) {
      this.cart = cart;
      this.config = config;
      this.dom = {};
      this.isOpen = false;
      this.isCheckingOut = false;
      this._init();
    }
  
    _init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._setup());
      } else { this._setup(); }
    }
  
    async _setup() {
      this._cacheDOMElements();
      this._ensureDrawerClosed();
      this._preventFormSubmissions();
      this._attachListeners();
  
      // Hydrate from Shopify if virtual cart is empty (handles V3→V4 migration)
      await this.cart.hydrateFromShopify();
  
      // Default shipping protection ON
      if (!this.cart.hasShippingProtection()) this.cart.setShippingProtection(true);
      // Evaluate gifts on startup
      this.cart.evaluateGifts();
      this.cart.onChange(() => { void this.render().catch(() => {}); });
      void this.render().catch(() => {});

      // Reset frozen checkout state when returning via browser back/forward (bfcache)
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) {
          this._resetCheckoutState();
        }
      });
    }

    /**
     * Resets checkout button and drawer from "Processing..." frozen state.
     * Called on pageshow (back/forward navigation from checkout).
     */
    _resetCheckoutState() {
      this.isCheckingOut = false;

      const btn = this.dom.checkoutBtn;
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'CHECKOUT';
      }

      this.dom.drawerOuter?.classList.remove('is-loading');
    }
  
    _cacheDOMElements() {
      this.dom = {
        drawerOuter: document.querySelector('.m-cart-v3-drawer-outer'),
        drawer: document.querySelector('.m-cart-v3-drawer'),
        closeBtn: document.querySelector('.md_cart_close'),
        cartCount: document.querySelector('.md_cart_info_qty'),
        cartItems: document.querySelector('.m-cart-v3-items'),
        itemsContainer: document.querySelector('.m-cart-v3-items-container'),
        emptyState: document.querySelector('.m-cart-v3-empty'),
        upsellsList: document.querySelector('.m-cart-v3-upsells-list'),
        upsellsContainer: document.querySelector('.m-cart-v3-upsells'),
        progressFill: document.querySelector('.m-cart-v3-progress-fill'),
        progressMessages: {
          locked: document.querySelector('.m-cart-v3-progress-message'),
          subscription: document.querySelector('.m-cart-v3-progress-message-subscription'),
          unlocked: document.querySelector('.m-cart-v3-progress-message-unlocked')
        },
        subtotal: document.querySelector('.subtotal_price__js'),
        subtotalCompare: document.querySelector('.subtotal_compare_price__js'),
        discount: document.querySelector('.discount_price__js'),
        discountLine: document.querySelector('.noBorder_line'),
        checkoutBtn: document.querySelector('.md_checkout_btn'),
        shippingSwitch: document.querySelector('.md_cart_gua_switch')
      };
    }
  
    _ensureDrawerClosed() {
      if (!this.dom.drawerOuter) return;
      this.dom.drawerOuter.style.display = 'none';
      this.dom.drawerOuter.classList.remove('active');
      this.dom.drawer?.classList.remove('active');
      document.documentElement.classList.remove('overflow-hidden');
    }
  
    _preventFormSubmissions() {
      const drawer = this.dom.drawer;
      if (!drawer) return;
      const prevent = (e) => {
        const form = e.target.closest('form');
        if (form && form.dataset.allowSubmit !== 'true') {
          e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
          return false;
        }
      };
      drawer.addEventListener('submit', prevent, true);
      if (typeof MutationObserver !== 'undefined') {
        new MutationObserver((mutations) => {
          mutations.forEach((mut) => {
            mut.addedNodes.forEach((node) => {
              if (node.nodeType !== 1) return;
              const forms = node.tagName === 'FORM' ? [node]
                : Array.from(node.querySelectorAll?.('form') || []);
              forms.forEach((f) => { if (f.dataset.allowSubmit !== 'true') f.addEventListener('submit', prevent, true); });
            });
          });
        }).observe(drawer, { childList: true, subtree: true });
      }
    }
  
    // ── Drawer ──
  
    open() {
      if (!this.dom.drawerOuter) this._cacheDOMElements();
      const outer = this.dom.drawerOuter;
      if (!outer) return;
      this.isOpen = true;
      outer.style.display = 'flex';
      document.documentElement.classList.add('overflow-hidden');
      requestAnimationFrame(() => {
        outer.classList.add('active');
        this.dom.drawer?.classList.add('active');
      });
    }
  
    close() {
      if (!this.dom.drawerOuter) return;
      this.isOpen = false;
      this.dom.drawerOuter.classList.remove('active');
      this.dom.drawer?.classList.remove('active');
      document.documentElement.classList.remove('overflow-hidden');
      setTimeout(() => { if (this.dom.drawerOuter) this.dom.drawerOuter.style.display = 'none'; }, 300);
    }
  
    // ── Events ──
  
    _attachListeners() {
      // Cart icon — capture phase to intercept theme JS
      document.addEventListener('click', (e) => {
        if (e.target.closest('.header__icon--cart, #cart-icon-bubble, [data-cart-toggle]')) {
          e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
          this.open(); return false;
        }
      }, true);
  
      this.dom.closeBtn?.addEventListener('click', () => this.close());
      this.dom.drawerOuter?.addEventListener('click', (e) => {
        if (!e.target.closest('.m-cart-v3-drawer')) this.close();
      });
      this.dom.checkoutBtn?.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation(); this._handleCheckout();
      });
  
      // Delegated clicks
      document.addEventListener('click', (e) => {
        // Upsell add
        const uBtn = e.target.closest('.m-cart-v3-upsell-add-button');
        if (uBtn) { e.preventDefault(); e.stopPropagation(); this._handleUpsellAdd(uBtn); return; }
  
        if (!e.target.closest('.m-cart-v3-drawer')) return;
        if (this.isCheckingOut) { e.preventDefault(); return; }
  
        // Remove item
        const rm = e.target.closest('.m-cart-v3-item-remove');
        if (rm) {
          e.preventDefault();
          const id = rm.dataset.lineId;
          if (id) {
            this.cart.removeItem(id);
            this.cart.evaluateGifts();
          }
          return;
        }
  
        // Subscription toggle
        const sub = e.target.closest('.m-cart-v3-item-subscription-toggle, .m-cart-v3-item-subscription-label');
        if (sub) { e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation(); this._handleSubToggle(sub); return; }
  
        // Shipping switch
        const sp = e.target.closest('.md_cart_gua_switch');
        if (sp) { e.preventDefault(); sp.classList.toggle('active'); const isOn = sp.classList.contains('active'); this.cart.setShippingProtection(isOn); return; }
      });
  
      // Quantity / variant dropdown
      document.addEventListener('change', (e) => {
        const dd = e.target.closest('.m-cart-v3-qty-dropdown');
        if (!dd) return;
        e.stopPropagation();
  
        const lineId = dd.dataset.lineId;
        // Variant switch mode: data-current-variant-id is set
        if (dd.dataset.currentVariantId) {
          const currentVid = parseInt(dd.dataset.currentVariantId, 10);
          const newVid = parseInt(dd.value, 10);
          if (lineId && newVid && newVid !== currentVid) {
            this._handleVariantSwitch(lineId, newVid, dd);
            dd.dataset.currentVariantId = String(newVid);
          }
        }
        // Quantity mode: data-current-qty is set
        else if (dd.dataset.currentQty !== undefined) {
          const newQty = parseInt(dd.value, 10);
          if (lineId && !Number.isNaN(newQty)) {
            this.cart.updateQuantity(lineId, newQty);
            dd.dataset.currentQty = String(newQty);
            this.cart.evaluateGifts();
          }
        }
      });
  
      // Custom PDP event
      document.addEventListener('cart-v4:add', (e) => this._handleExternalAdd(e.detail));
  
      // ── Global product form intercept ──
      // Catches ALL product form submissions (original picker, v3 Kaching, etc.)
      // and routes them through the virtual cart + drawer instead of native checkout
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!form || !form.action || !form.action.includes('/cart/add')) return;
        if (form.dataset.allowSubmit === 'true') return;
  
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
  
        const variantInput = form.querySelector('[name="id"]');
        const qtyInput = form.querySelector('[name="quantity"]');
        const spInput = form.querySelector('[name="selling_plan"]');
        const vid = parseInt(variantInput?.value, 10);
        if (!vid) return;
  
        const qty = parseInt(qtyInput?.value, 10) || 1;
        const sp = spInput?.value ? parseInt(spInput.value, 10) : null;
  
        document.dispatchEvent(new CustomEvent('cart-v4:add', {
          detail: { variantId: vid, quantity: qty, sellingPlan: sp || undefined }
        }));
      }, true);
    }
  
    // ── Render ──
  
    async render() {
      const items = this.cart.getRealItems();
      let imagePersist = false;
      await Promise.all(items.map(async (item) => {
        if (!item.handle) return;
        const next = await resolveCollectionImageUrl(item.handle, item.image || '');
        if (next !== item.image) {
          item.image = next;
          imagePersist = true;
        }
      }));
      if (imagePersist) this.cart._save();

      const gifts = this.cart.getGiftItems();
      const count = this.cart.getRealItemCount();
      const subtotal = this.cart.getSubtotal();

      this._updateCount(count);
      if (count === 0) { this._showEmpty(); } else { this._hideEmpty(); this._renderItems(items, gifts); }
      this._renderProgress(subtotal);
      this._renderSummary();
      this._renderUpsells(count);
      this._syncSPSwitch();
    }
  
    /**
     * Renders all items + gifts into the cart items container.
     * Matches V3 Liquid structure: real items with subscription toggles,
     * then general gifts section at the bottom.
     */
    _renderItems(items, gifts) {
      const c = this.dom.cartItems;
      if (!c) return;
  
      // Group kit gifts by their trigger product ID
      const kitGiftsByTrigger = new Map();
      const generalGifts = [];
      gifts.forEach((gift) => {
        if (gift.giftLayout === 'kit' && gift.giftTriggerProductId) {
          const key = String(gift.giftTriggerProductId);
          if (!kitGiftsByTrigger.has(key)) kitGiftsByTrigger.set(key, []);
          kitGiftsByTrigger.get(key).push(gift);
        } else {
          generalGifts.push(gift);
        }
      });
  
      let html = '';
  
      // Real items — pass kit gifts for inline rendering inside item-bottom
      items.forEach((item) => {
        const pid = String(item.productId);
        const kitGifts = kitGiftsByTrigger.get(pid) || [];
        html += this._itemHTML(item, kitGifts);
      });
  
      // General gifts section at bottom
      if (generalGifts.length > 0) {
        html += '<div class="m-cart-v3-general-gifts-section"><div class="m-cart-v3-general-gifts-list">';
        generalGifts.forEach((gift) => {
          html += `
            <div class="m-cart-v3-general-gift" data-line-id="${gift.id}" data-variant-id="${gift.variantId}">
              <div class="m-cart-v3-general-gift-image">
                ${gift.image ? `<img src="${this._ea(gift.image)}" alt="${this._ea(gift.title)}" loading="lazy" width="32" height="32">` : ''}
              </div>
              <div class="m-cart-v3-general-gift-info">
                <h4 class="m-cart-v3-general-gift-title">${this._eh(gift.title)}</h4>
              </div>
              <div class="m-cart-v3-general-gift-price">
                <span class="m-cart-v3-general-gift-free">FREE</span>
              </div>
            </div>`;
        });
        html += '</div></div>';
      }
  
      c.innerHTML = html;
    }
  
    /**
     * Generates V3-compatible item HTML.
     * Matches the Liquid output from m-cart-v3-item-top.liquid + m-cart-v3-item-subscription-toggle.liquid
     */
    _itemHTML(item, kitGifts = []) {
      const linePrice = this._fmt(item.price * item.quantity);
      const isSub = item.sellingPlan != null;
      const hasCmp = item.compareAtPrice && item.compareAtPrice > item.price;
      const cmpPrice = hasCmp ? this._fmt(item.compareAtPrice * item.quantity) : '';
      const disc = item.subscriptionDiscount || 0;
  
      // Variant subtitle: matches PDP picker format "Every X Month(s)"
      let variantSubtitle = 'One-Time Purchase';
      if (isSub) {
        const vt = (item.variantTitle || '').toLowerCase();
        let months = 1; // default
        const dayMatch = vt.match(/(\d+)\s*day/);
        const packMatch = vt.match(/^(\d+)\s*(?:bottle|bag)/i);
        if (dayMatch) {
          months = Math.round(parseInt(dayMatch[1], 10) / 30);
        } else if (packMatch) {
          months = parseInt(packMatch[1], 10);
        }
        if (months < 1) months = 1;
        variantSubtitle = `${months}-Month Supply Every ${months} ${months === 1 ? 'Month' : 'Months'}`;
      }
  
      // Variant dropdown — uses variant data if available, falls back to quantity
      const dropdownHTML = this._buildDropdownHTML(item);
  
      // Kit gifts HTML — rendered inside item-bottom, before subscription toggle
      let kitGiftsHTML = '';
      if (kitGifts.length > 0) {
        kitGiftsHTML = '<div class="m-cart-v3-kit-gifts">';
        kitGifts.forEach((gift) => {
          kitGiftsHTML += `
            <div class="m-cart-v3-kit-gift" data-line-id="${gift.id}" data-variant-id="${gift.variantId}">
              <div class="m-cart-v3-kit-gift-image">
                ${gift.image ? `<img src="${this._ea(gift.image)}" alt="${this._ea(gift.title)}" loading="lazy" width="40" height="40">` : ''}
              </div>
              <div class="m-cart-v3-kit-gift-info">
                <h5 class="m-cart-v3-kit-gift-title">${this._eh(gift.title)}</h5>
              </div>
              <div class="m-cart-v3-kit-gift-price">
                <span class="m-cart-v3-kit-gift-free">FREE</span>
              </div>
            </div>`;
        });
        kitGiftsHTML += '</div>';
      }
  
      // Subscription toggle — matches V3 Liquid logic exactly
      const subToggleHTML = this._buildSubToggleHTML(item, isSub, disc);

      // Build item-bottom: kit gifts first, then subscription toggle (matches old Liquid)
      const itemBottomHTML = (kitGiftsHTML || subToggleHTML) ? `
          <div class="m-cart-v3-item-bottom">
            ${kitGiftsHTML}
            ${subToggleHTML}
          </div>` : '';

      const lineImgSrc = getLineItemDisplayImage(item.handle, item.image);

      return `
        <div class="m-cart-v3-item${isSub ? ' has-divider' : ''}" data-line-id="${item.id}" data-variant-id="${item.variantId}" data-product-id="${item.productId || ''}">
          <div class="m-cart-v3-item-top">
            <div class="m-cart-v3-item-image">
              ${lineImgSrc ? `<img src="${this._ea(lineImgSrc)}" alt="${this._ea(item.title)}" width="80" height="80" loading="lazy">` : ''}
            </div>
            <div class="m-cart-v3-item-details">
              <div class="m-cart-v3-item-header">
                <div class="m-cart-v3-item-info">
                  <h4 class="m-cart-v3-item-title">${this._eh(item.title)}</h4>
                  <p class="m-cart-v3-item-variant">${this._eh(variantSubtitle)}</p>
                </div>
                <div class="m-cart-v3-item-price-section">
                  <div class="m-cart-v3-item-prices">
                    <span class="m-cart-v3-item-price">${linePrice}</span>
                    ${hasCmp ? `<span class="m-cart-v3-item-compare-price">${cmpPrice}</span>` : ''}
                  </div>
                </div>
              </div>
              <div class="m-cart-v3-item-actions">
                <div class="m-cart-v3-qty-dropdown-wrapper">
                  ${dropdownHTML}
                  <svg class="m-cart-v3-qty-dropdown-chevron" width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M0.182783 0.198844C0.0657412 0.316032 0 0.474885 0 0.640511C0 0.806136 0.0657412 0.964989 0.182783 1.08218L3.95778 4.85718L7.73278 1.08218C7.79419 1.02496 7.84344 0.955959 7.8776 0.879292C7.91176 0.802627 7.93013 0.719865 7.93161 0.635946C7.93309 0.552027 7.91765 0.468669 7.88622 0.390846C7.85478 0.313023 7.808 0.242328 7.74865 0.182979C7.6893 0.12363 7.6186 0.0768428 7.54078 0.0454087C7.46296 0.0139742 7.3796 -0.00146294 7.29568 1.7643e-05C7.21176 0.00149822 7.129 0.0198665 7.05233 0.0540266C6.97567 0.0881867 6.90667 0.137439 6.84945 0.198844L3.95778 3.09051L1.06612 0.198844C0.948929 0.0818024 0.790076 0.0160608 0.62445 0.0160608C0.458824 0.0160608 0.299971 0.0818024 0.182783 0.198844Z" fill="#6E768C"/>
                  </svg>
                </div>
                <button class="m-cart-v3-item-remove" aria-label="Remove item" data-line-id="${item.id}">
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M0.468844 2.43831H13.6864C13.9453 2.43831 14.1552 2.22844 14.1552 1.96956C14.1552 1.71069 13.9453 1.50081 13.6864 1.50081H10.0257V1.20675C10.0257 0.874125 9.89022 0.572375 9.67175 0.353906C9.45328 0.135438 9.1515 0 8.81887 0H5.33634C5.00369 0 4.70194 0.135438 4.48347 0.353906C4.26503 0.572375 4.12956 0.874125 4.12956 1.20675V1.50081H0.468844C0.209969 1.50081 9.36985e-05 1.71069 9.36985e-05 1.96956C9.36985e-05 2.22844 0.209969 2.43831 0.468844 2.43831ZM5.06709 1.20675C5.06709 1.13291 5.09744 1.06566 5.14634 1.01675C5.19525 0.967844 5.26253 0.9375 5.33637 0.9375H8.81891C8.89275 0.9375 8.96003 0.967844 9.00894 1.01675C9.05784 1.06566 9.08819 1.13294 9.08819 1.20675V1.50081H5.06709V1.20675ZM13.6865 3.16834H0.46875C0.209875 3.16834 0 3.37822 0 3.63709C0 3.89597 0.209875 4.10584 0.46875 4.10584H1.35684L2.60437 13.8866C2.65162 14.2572 2.96278 14.5312 3.33647 14.5312H10.8187C11.1924 14.5312 11.5036 14.2572 11.5508 13.8866L12.7984 4.10584H13.6865C13.9453 4.10584 14.1552 3.89597 14.1552 3.63709C14.1552 3.37822 13.9453 3.16834 13.6865 3.16834H13.6865ZM4.91319 12.6359C4.65531 12.6561 4.42987 12.4635 4.40966 12.2056L3.90941 6.03597C3.88919 5.77809 4.08184 5.55266 4.33972 5.53244C4.59756 5.51222 4.82303 5.70487 4.84325 5.96272L5.3435 12.1324C5.36372 12.3902 5.17106 12.6157 4.91319 12.6359ZM7.54637 12.169C7.54637 12.4279 7.3365 12.6377 7.07762 12.6377C6.81875 12.6377 6.60887 12.4279 6.60887 12.169V5.99937C6.60887 5.7405 6.81875 5.53062 7.07762 5.53062C7.3365 5.53062 7.54637 5.7405 7.54637 5.99937V12.169V12.169ZM10.2458 6.03597L9.74562 12.2056C9.72541 12.4635 9.49994 12.6562 9.24209 12.6359C8.98422 12.6157 8.79156 12.3902 8.81178 12.1324L9.312 5.96272C9.33222 5.70487 9.55769 5.51219 9.81553 5.53244C10.0734 5.55266 10.2661 5.77812 10.2458 6.03597Z" fill="#6E768C"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          ${itemBottomHTML}
        </div>`;
    }
  
    /**
     * Builds the dropdown HTML.
     * If item has variant data → variant-switch dropdown (Kaching deal bars or Shopify variants).
     * Otherwise → quantity dropdown with 1-10.
     * Also triggers lazy variant loading for items without variants.
     */
    _buildDropdownHTML(item) {
      // If we have variants stored → variant switch dropdown
      if (item.variants && item.variants.length > 1) {
        const opts = item.variants
          .filter((v) => v.available !== false)
          .map((v) => {
            const vid = v.id;
            const label = v.title || `Variant ${vid}`;
            const selected = String(vid) === String(item.variantId) ? ' selected' : '';
            return `<option value="${vid}"${selected}>${this._eh(label)}</option>`;
          })
          .join('');
        return `<select class="m-cart-v3-qty-dropdown" data-line-id="${item.id}" data-current-variant-id="${item.variantId}" aria-label="Quantity">${opts}</select>`;
      }
  
      // No variants stored — trigger lazy load from Shopify API
      if (item.handle && (!item.variants || item.variants.length === 0)) {
        this._lazyLoadVariants(item);
      }
  
      // Fallback: quantity dropdown 1-10
      const opts = Array.from({ length: 10 }, (_, i) => i + 1)
        .map((n) => `<option value="${n}"${n === item.quantity ? ' selected' : ''}>${n}</option>`).join('');
      return `<select class="m-cart-v3-qty-dropdown" data-line-id="${item.id}" data-current-qty="${item.quantity}" aria-label="Quantity">${opts}</select>`;
    }
  
    /**
     * Lazily fetches variants for an item that has none stored.
     * Fetches from /products/{handle}.js, filters by bundleQuantities stored on item,
     * normalizes and stores on the item, then re-renders.
     */
    async _lazyLoadVariants(item) {
      if (!item.handle || item._variantsLoading) return;
      item._variantsLoading = true;
      try {
        const product = await this.cart.priceResolver.fetchProduct(item.handle);
        if (!product?.variants || product.variants.length <= 1) return;
  
        let variants = product.variants;
  
        // Try item-level bundleQuantities first (set at add-time from card or PDP)
        const bq = item.bundleQuantities || ProductScraper._getBundleQuantities(product.id);
        if (bq && bq.length > 0) {
          const filtered = ProductScraper._filterVariantsByBundleQty(variants, bq);
          if (filtered) variants = filtered;
        }
  
        // Normalize to our format (.js endpoint: prices in cents, has selling_plan_allocations)
        item.variants = variants.map((v) => {
          const alloc = v.selling_plan_allocations?.[0];
          return {
            id: v.id,
            title: ProductScraper._cleanTitle(v.title || (v.options ? v.options.join(' / ') : `Variant ${v.id}`)),
            available: v.available !== false,
            price: v.price || 0,
            compareAtPrice: v.compare_at_price || null,
            sellingPlanId: alloc?.selling_plan_id || null,
            subscriptionPrice: alloc?.per_delivery_price || null
          };
        });
  
        // Save updated item and re-render
        this.cart._save();
        void this.render().catch(() => {});
      } catch { /* silent */ } finally {
        item._variantsLoading = false;
      }
    }
  
    /**
     * Builds subscription toggle HTML.
     * Matches V3 Liquid exactly:
     * - If item IS subscribed → show "Change to one-time" toggle (checked state, --one-time modifier)
     * - If item is NOT subscribed but HAS subscription available → show "Subscribe & save" toggle
     * - If no subscription available → no toggle
     */
    _buildSubToggleHTML(item, isSub, disc) {
      const planId = item.sellingPlanId || item.originalSellingPlanId || '';
      const checkSVG = '<svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.98155 5.85817L0.981176 3.8578C0.873388 3.75001 0.727195 3.68945 0.574759 3.68945C0.422324 3.68945 0.276131 3.75001 0.168343 3.8578C0.0605548 3.96558 0 4.11178 0 4.26421C0 4.33969 0.0148667 4.41443 0.0437511 4.48416C0.0726355 4.5539 0.114972 4.61726 0.168343 4.67063L2.57802 7.0803C2.80284 7.30513 3.16602 7.30513 3.39085 7.0803L9.48998 0.981176C9.59777 0.873388 9.65832 0.727195 9.65832 0.57476C9.65832 0.422324 9.59777 0.276132 9.48998 0.168343C9.38219 0.060555 9.236 0 9.08356 0C8.93113 0 8.78493 0.060555 8.67715 0.168343L2.98155 5.85817Z" fill="#1C2B54"/></svg>';
  
      if (isSub) {
        return `
            <button type="button" class="m-cart-v3-item-subscription-toggle m-cart-v3-item-subscription-toggle--one-time" data-line-id="${item.id}" data-selling-plan-id="${planId}">
              <div class="m-cart-v3-item-subscription-checkbox checked">
                ${checkSVG}
              </div>
              <span class="m-cart-v3-item-subscription-label">Change to one-time</span>
            </button>`;
      }
  
      if (item.subscriptionPrice && item.subscriptionPrice > 0 && planId) {
        let displayDisc = disc;
        const subPrice = item.subscriptionPrice;
        const currentVariant = item.variants?.find((v) => String(v.id) === String(item.variantId));
        const compareAt = currentVariant?.compareAtPrice || item.originalCompareAtPrice || 0;
        if (compareAt > 0 && subPrice > 0 && compareAt > subPrice) {
          displayDisc = Math.round((1 - subPrice / compareAt) * 100);
        }
  
        return `
            <button type="button" class="m-cart-v3-item-subscription-toggle" data-line-id="${item.id}" data-selling-plan-id="${planId}">
              <div class="m-cart-v3-item-subscription-checkbox">
                ${checkSVG}
              </div>
              <span class="m-cart-v3-item-subscription-label">Subscribe &amp; save</span>
              ${displayDisc > 0 ? `<span class="m-cart-v3-item-subscription-discount">-${displayDisc}%</span>` : ''}
            </button>`;
      }
  
      // No subscription available
      return '';
    }
  
    // ── Progress ──
  
    _renderProgress(subtotal) {
      if (!this.dom.progressFill) return;
      const threshold = this.config.freeShippingThreshold;
  
      if (this.cart.hasSubscription()) {
        this.dom.progressFill.style.width = '100%';
        this._showMsg('subscription'); return;
      }
      if (subtotal >= threshold) {
        this.dom.progressFill.style.width = '100%';
        this._showMsg('unlocked'); return;
      }
  
      this.dom.progressFill.style.width = `${Math.min((subtotal / threshold) * 100, 100)}%`;
      const remaining = (threshold - subtotal) / 100;
      const el = this.dom.progressMessages.locked;
      if (el) {
        const tpl = el.getAttribute('data-template') || "You're {amount} away from FREE SHIPPING!";
        const parts = tpl.split('{amount}');
        el.textContent = '';
        if (parts[0]) el.appendChild(document.createTextNode(parts[0]));
        const s = document.createElement('strong');
        s.className = 'm-cart-v3-progress-amount';
        s.textContent = `${this.config.currency}${remaining.toFixed(2)}`;
        el.appendChild(s);
        if (parts[1]) el.appendChild(document.createTextNode(parts[1]));
      }
      this._showMsg('locked');
    }
  
    _showMsg(state) {
      Object.entries(this.dom.progressMessages).forEach(([k, el]) => {
        if (!el) return;
        el.style.display = k === state ? 'block' : 'none';
        if (k === state) el.setAttribute('data-state', 'visible');
        else el.removeAttribute('data-state');
      });
    }
  
    // ── Summary ──
  
    _renderSummary() {
      const itemsSubtotal = this.cart.getSubtotal();
      const spPrice = this.cart.getShippingProtectionPrice();
      const displaySubtotal = itemsSubtotal + spPrice;
      const cmp = this.cart.getCompareTotal();
      const sav = this.cart.getTotalSavings();
  
      if (this.dom.subtotal) this.dom.subtotal.textContent = this._fmt(displaySubtotal);
      if (this.dom.subtotalCompare) {
        if (cmp > itemsSubtotal) {
          this.dom.subtotalCompare.textContent = this._fmt(cmp + spPrice);
          this.dom.subtotalCompare.style.display = 'inline';
        } else { this.dom.subtotalCompare.style.display = 'none'; }
      }
      if (this.dom.discount && this.dom.discountLine) {
        if (sav > 0) { this.dom.discount.textContent = this._fmt(sav); this.dom.discountLine.style.display = 'flex'; }
        else this.dom.discountLine.style.display = 'none';
      }
    }
  
    // ── Empty / Non-empty state ──
  
    _showEmpty() {
      if (this.dom.emptyState) this.dom.emptyState.style.display = 'block';
      if (this.dom.itemsContainer) this.dom.itemsContainer.style.display = 'none';
    }
  
    _hideEmpty() {
      if (this.dom.emptyState) this.dom.emptyState.style.display = 'none';
      if (this.dom.itemsContainer) this.dom.itemsContainer.style.display = 'block';
    }
  
    // ── Upsells ──
  
    _renderUpsells(count) {
      const list = this.dom.upsellsList;
      const container = this.dom.upsellsContainer;
      if (!list) return;
  
      const cartPids = this.cart.getCartProductIds();
      const cartVids = this.cart.getCartVariantIds();
  
      const allCards = Array.from(list.querySelectorAll('.m-cart-v3-upsell-card'));
      const p3Wrapper = list.querySelector('.m-cart-v3-upsell-product-3');
  
      // Mark cards as excluded if product/variant is in virtual cart
      allCards.forEach((c) => {
        const pid = c.dataset.productId;
        const vid = c.dataset.variantId;
        const inCart = (pid && cartPids.has(pid)) || (vid && cartVids.has(vid));
        c.classList.toggle('m-cart-v3-upsell-excluded', inCart);
        c.style.display = 'none';
      });
  
      // Target: 3 when virtual cart empty, 2 when has items
      const maxVisible = count === 0 ? 3 : 2;
  
      const visible = [];
      for (const card of allCards) {
        if (visible.length >= maxVisible) break;
        if (!card.classList.contains('m-cart-v3-upsell-excluded')) {
          card.style.display = '';
          visible.push(card);
        }
      }
  
      if (p3Wrapper) {
        const hasVisibleChild = visible.some((c) => p3Wrapper.contains(c));
        p3Wrapper.style.display = hasVisibleChild ? 'block' : 'none';
      }
  
      visible.forEach((c, i) => {
        c.style.borderBottom = i < visible.length - 1 ? '1px solid #EDEDF3' : 'none';
      });
  
      if (list) list.classList.toggle('has-2-products', count > 0);
      if (container) container.style.display = visible.length > 0 ? 'block' : 'none';
    }
  
    // ── Misc ──
  
    _syncSPSwitch() {
      const sw = this.dom.shippingSwitch;
      if (sw) sw.classList.toggle('active', this.cart.hasShippingProtection());
    }
  
    _updateCount(n) {
      if (this.dom.cartCount) this.dom.cartCount.textContent = n;
      const b = document.querySelector('.cart-count-bubble span');
      if (b) { b.textContent = n; const c = b.closest('.cart-count-bubble'); if (c) c.style.display = ''; }
      document.querySelectorAll('[data-cart-count], .cart-count').forEach((el) => { el.textContent = n; });
    }
  
    // ── Handlers ──
  
    /**
     * Handles subscription toggle click.
     * Two modes matching V3 Liquid:
     * 1. --one-time toggle (item IS subscribed) → clicking converts to one-time
     * 2. Regular toggle (item NOT subscribed) → clicking adds subscription
     */
    _handleSubToggle(target) {
      const toggle = target.classList.contains('m-cart-v3-item-subscription-toggle')
        ? target : target.closest('.m-cart-v3-item-subscription-toggle');
      if (!toggle) return;
  
      const lineId = toggle.dataset.lineId;
      const planId = toggle.dataset.sellingPlanId;
      const isOneTimeToggle = toggle.classList.contains('m-cart-v3-item-subscription-toggle--one-time');
      if (!lineId) return;
  
      if (isOneTimeToggle) {
        this.cart.toggleSubscription(lineId, null);
      } else {
        if (planId) {
          this.cart.toggleSubscription(lineId, parseInt(planId, 10));
        }
      }
  
      this.cart.evaluateGifts();
    }
  
    async _handleVariantSwitch(lineId, newVid, dd) {
      const item = this.cart.items.find((i) => i.id === lineId);
      if (!item) return;
  
      const title = dd.options[dd.selectedIndex]?.textContent?.trim() || item.variantTitle;
  
      const storedVariant = item.variants?.find((v) => String(v.id) === String(newVid));
      if (storedVariant && storedVariant.price) {
        const pd = {
          regularPrice: storedVariant.price,
          subscriptionPrice: storedVariant.subscriptionPrice || null,
          subscriptionDiscount: 0,
          compareAtPrice: storedVariant.compareAtPrice || null
        };
        this.cart.switchVariant(lineId, newVid, title, pd, storedVariant.sellingPlanId || null);
      } else {
        const pd = await this.cart.priceResolver.resolve(newVid, item.handle);
        this.cart.switchVariant(lineId, newVid, title, pd, null);
      }
  
      this.cart.evaluateGifts();
    }
  
    /**
     * Handles upsell add with FULL product data from card DOM.
     * Also fetches product JSON for variant data (bundle dropdown).
     */
    async _handleUpsellAdd(btn) {
      if (btn.disabled) return;
      const vid = parseInt(btn.dataset.variantId || btn.getAttribute('data-variant-id'), 10);
      if (!vid) return;
      const card = btn.closest('.m-cart-v3-upsell-card');
      if (!card) return;
  
      const productData = ProductScraper.fromUpsellCard(card, vid);
      // Upsells always add as OTP — customer can toggle to subscription from the cart item
      const planId = null;
  
      const orig = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding...';
  
      let variants = [];
      let bundleQuantities = null;
      const handle = productData.handle;
  
      // Read bundle_quantities from the upsell card (set by Liquid from metafield)
      const bqAttr = card.dataset.bundleQuantities;
      if (bqAttr) {
        bundleQuantities = bqAttr.split(',').map((n) => parseInt(n.trim(), 10)).filter((n) => n > 0);
      }
  
      if (handle) {
        try {
          const root = window.Shopify?.routes?.root || '/';
          const res = await fetch(`${root}products/${handle}.js`);
          if (res.ok) {
            const product = await res.json();
            if (product?.variants && product.variants.length > 1) {
              let source = product.variants;
  
              if (bundleQuantities && bundleQuantities.length > 0) {
                const filtered = ProductScraper._filterVariantsByBundleQty(product.variants, bundleQuantities);
                if (filtered) source = filtered;
              }
  
              variants = source.map((v) => {
                const alloc = v.selling_plan_allocations?.[0];
                const spId = alloc?.selling_plan_id || null;
                const spPrice = alloc?.per_delivery_price || null;
                let title = v.title || '';
                const dashIdx = title.lastIndexOf(' - ');
                if (dashIdx > 0) title = title.substring(dashIdx + 3);
                return {
                  id: v.id,
                  title,
                  available: v.available !== false,
                  price: v.price || 0,
                  compareAtPrice: v.compare_at_price || null,
                  sellingPlanId: spId,
                  subscriptionPrice: spPrice
                };
              });
            }
          }
        } catch (err) { console.warn('[V4] Upsell variant fetch failed:', err); }
      }
  
      this.cart.addItem({
        variantId: vid,
        productId: productData.productId,
        handle,
        quantity: 1,
        sellingPlan: planId,
        priceData: productData.priceData,
        title: productData.title,
        variantTitle: productData.variantTitle,
        image: productData.image,
        productUrl: productData.productUrl,
        sellingPlanId: productData.sellingPlanId,
        variants,
        bundleQuantities
      });
  
      this.cart.evaluateGifts();
  
      if (!this.isOpen) this.open();
      btn.textContent = 'Added!';
      setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 2000);
    }
  
    /**
     * Handles `cart-v4:add` custom event from PDP.
     */
    async _handleExternalAdd(d) {
      if (!d?.variantId) return;
  
      // If full data provided, use it directly
      if (d.title && d.priceData) {
        this.cart.addItem(d);
        this.open();
        return;
      }
  
      // Normalize: Kaching sends sellingPlanId, we need sellingPlan for the active plan
      const sellingPlanId = d.sellingPlanId || d.sellingPlan || null;
      const activePlan = sellingPlanId ? parseInt(sellingPlanId, 10) : null;
  
      const pdpData = ProductScraper.fromPDP(d.variantId);
      const handle = d.handle || pdpData?.handle || ProductScraper._getHandleFromURL();
      let priceData = this.cart.priceResolver.captureFromDOM(d.variantId);
      let fullProduct = null;
  
      if (handle) {
        fullProduct = await this.cart.priceResolver.resolveFullProduct(d.variantId, handle);
      }
  
      if (!priceData && fullProduct) priceData = fullProduct.priceData;
      if (!priceData) priceData = { regularPrice: 0, subscriptionPrice: null, subscriptionDiscount: 0, compareAtPrice: null };
  
      // Always resolve subscription data from /products/{handle}.js
      // so the toggle works even when added as OTP
      let resolvedPlanId = activePlan;
      if (!priceData.subscriptionPrice && handle) {
        const spData = await this._resolveSubscriptionPrice(handle, d.variantId, activePlan);
        if (spData) {
          priceData.subscriptionPrice = spData.price;
          priceData.subscriptionDiscount = spData.discount;
          if (!resolvedPlanId && spData.planId) {
            resolvedPlanId = spData.planId;
          }
        }
      }
  
      const productId = d.productId || pdpData?.productId || fullProduct?.productId || null;
      const bundleQuantities = productId ? ProductScraper._getBundleQuantities(productId) : null;
  
      // Build authoritative variant list from data-picker-variants (Kaching's source of truth on PDP)
      let pdpVariants = pdpData?.variants || fullProduct?.variants || [];
      const pickerDataEl = document.querySelector("[data-picker-variants]");
      if (pickerDataEl) {
        try {
          const pickerData = JSON.parse(pickerDataEl.textContent);
          if (pickerData?.variants?.length > 0) {
            pdpVariants = pickerData.variants.map((v) => ({
              id: v.id,
              title: ProductScraper._cleanTitle(v.title || `Variant ${v.id}`),
              available: true,
              price: v.price || 0,
              compareAtPrice: v.compare_at_price || null,
              sellingPlanId: v.selling_plan_id || null,
              subscriptionPrice: v.subscription_price || null
            }));
          }
        } catch { /* use fallback variants */ }
      }
  
      this.cart.addItem({
        variantId: d.variantId,
        productId,
        handle: handle,
        quantity: d.quantity || 1,
        sellingPlan: activePlan,
        priceData,
        title: d.title || pdpData?.title || fullProduct?.title || '',
        variantTitle: d.variantTitle || pdpData?.variantTitle || fullProduct?.variantTitle || '',
        image: d.image || pdpData?.image || fullProduct?.image || '',
        productUrl: d.productUrl || pdpData?.productUrl || fullProduct?.productUrl || '',
        sellingPlanId: resolvedPlanId,
        variants: pdpVariants,
        bundleQuantities
      });
  
      this.open();
    }
  
    /**
     * Resolves subscription price for a variant from /products/{handle}.js
     * which includes selling_plan_allocations with per_delivery_price.
     */
    async _resolveSubscriptionPrice(handle, variantId, planId) {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const res = await fetch(`${root}products/${handle}.js`);
        if (!res.ok) return null;
        const product = await res.json();
  
        const variant = product.variants?.find((v) => v.id === parseInt(variantId, 10) || v.id === variantId);
        if (!variant?.selling_plan_allocations || variant.selling_plan_allocations.length === 0) return null;
  
        if (planId) {
          const alloc = variant.selling_plan_allocations.find((a) => a.selling_plan_id === planId);
          if (alloc) {
            const subPrice = alloc.per_delivery_price || alloc.price;
            const regPrice = variant.price;
            const discount = regPrice > 0 ? Math.round((1 - subPrice / regPrice) * 100) : 0;
            return { price: subPrice, discount, planId };
          }
        }
  
        const first = variant.selling_plan_allocations[0];
        const subPrice = first.per_delivery_price || first.price;
        const regPrice = variant.price;
        const discount = regPrice > 0 ? Math.round((1 - subPrice / regPrice) * 100) : 0;
        return { price: subPrice, discount, planId: first.selling_plan_id };
      } catch {
        return null;
      }
    }
  
    async _handleCheckout() {
      if (this.isCheckingOut || this.cart.getRealItemCount() === 0) return;
      this.isCheckingOut = true;
      const btn = this.dom.checkoutBtn;
      const orig = btn?.textContent;
      try {
        if (btn) { btn.disabled = true; btn.textContent = 'Processing...'; }
        this.dom.drawerOuter?.classList.add('is-loading');
        await this.cart.checkout();
      } catch (err) {
        console.error('[V4] Checkout error:', err);
        if (btn) { btn.disabled = false; btn.textContent = orig || 'CHECKOUT'; }
        this.dom.drawerOuter?.classList.remove('is-loading');
        this.isCheckingOut = false;
      }
    }
  
    // ── Utils ──
  
    _fmt(cents) { return `${this.config.currency}${(cents / 100).toFixed(2)}`; }
  
    _eh(s) {
      const d = document.createElement('div');
      d.appendChild(document.createTextNode(s || ''));
      return d.innerHTML;
    }
  
    _ea(s) {
      return (s || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
  }
  
  /* ═══════════════════════════════════════════════════════════════
     Init — reads V3 config, exposes V3-compatible API
     ═══════════════════════════════════════════════════════════════ */
  
  function initCartV4() {
    const cfgEl = document.getElementById('m-cart-v3-config');
    let config = {
      freeShippingThreshold: 5000, currency: '$',
      shippingProtectionVariantId: null, shippingProtectionPrice: 0,
      giftThreshold: 8000, giftRules: [],
      thresholdGift: null, subscriptionGift: null,
      collectionImageByHandle: {}
    };
    if (cfgEl) { try { config = { ...config, ...JSON.parse(cfgEl.textContent) }; } catch { /* */ } }
  
    const cart = new VirtualCart(config);
    const drawer = new CartDrawerUI(cart, config);
  
    /**
     * V3-compatible addToCart API.
     * Accepts just variantId (like V3) — resolves product data automatically.
     */
    async function addToCartCompat(variantId, quantity = 1, sellingPlan = null) {
      const vid = parseInt(variantId, 10);
      if (!vid) return;
  
      let productData = ProductScraper.fromPDP(vid);
      let priceData = cart.priceResolver.captureFromDOM(vid);
  
      const handle = productData?.handle || ProductScraper._getHandleFromURL()
        || window.ShopifyAnalytics?.meta?.product?.handle || '';
  
      let fullProduct = null;
      if (handle) {
        fullProduct = await cart.priceResolver.resolveFullProduct(vid, handle);
        if (fullProduct) {
          if (!productData) productData = fullProduct;
          if (!priceData) priceData = fullProduct.priceData;
          if (!productData.productId) productData.productId = fullProduct.productId;
        }
      }
  
      const pdpVariants = productData?.variants;
      const fpVariants = fullProduct?.variants;
      let finalVariants = [];
      if (pdpVariants && pdpVariants.length > 0) {
        finalVariants = pdpVariants;
      } else if (fpVariants && fpVariants.length > 0) {
        finalVariants = fpVariants;
      }
  
      cart.addItem({
        variantId: vid,
        productId: productData?.productId || null,
        handle: handle,
        quantity: quantity || 1,
        sellingPlan: sellingPlan ? parseInt(sellingPlan, 10) : null,
        priceData: priceData || { regularPrice: 0, subscriptionPrice: null, subscriptionDiscount: 0, compareAtPrice: null },
        title: productData?.title || '',
        variantTitle: productData?.variantTitle || '',
        image: productData?.image || '',
        productUrl: productData?.productUrl || '',
        sellingPlanId: sellingPlan ? parseInt(sellingPlan, 10) : null,
        variants: finalVariants
      });
  
      cart.evaluateGifts();
      drawer.open();
    }
  
    // Expose V3-compatible global API
    window.cartV3 = {
      open: () => drawer.open(),
      close: () => drawer.close(),
      refresh: () => { void drawer.render().catch(() => {}); },
      addToCart: addToCartCompat,
      _instance: { cart, drawer }
    };
    window.cartV4 = window.cartV3;
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartV4);
  } else { initCartV4(); }